/**
 * Reusable validation utilities for form fields
 * Used across signup, profile completion, and other forms
 */

/**
 * Validates a full name (first and last name)
 * Requirements: At least 2 words, each word at least 3 letters
 */
export function validateFullName(name: string): string | undefined {
  if (!name.trim()) {
    return "Full name is required";
  }

  const nameWords = name
    .trim()
    .split(/\s+/)
    .filter((word) => word.length > 0);

  if (nameWords.length < 2) {
    return "Please enter both first and last name (at least 2 words)";
  }

  if (nameWords.some((word) => word.length < 3)) {
    return "Each name must be at least 3 letters long";
  }

  // Allow letters, spaces, hyphens, and apostrophes
  if (!/^[a-zA-Z\s-']+$/.test(name)) {
    return "Name can only contain letters, spaces, hyphens, and apostrophes";
  }

  return undefined;
}

/**
 * Validates an email address
 */
export function validateEmail(email: string): string | undefined {
  if (!email.trim()) {
    return "Email address is required";
  }

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(email.trim())) {
    return "Please enter a valid email address";
  }

  return undefined;
}

/**
 * Formats phone number to ensure it starts with 234 and removes leading 0 after 234
 * @param phoneNumber - The phone number to format
 * @returns Formatted phone number (234XXXXXXXXX, 13 digits total)
 */
export function formatPhoneNumber(phoneNumber: string): string {
  // Remove any non-digit characters
  const digitsOnly = phoneNumber.replace(/\D/g, "");

  // If user deletes the country code, preserve it
  if (digitsOnly.length < 3) {
    return "234";
  }

  // If number doesn't start with 234, add it automatically
  if (!digitsOnly.startsWith("234")) {
    const phoneNumber = digitsOnly.slice(-10);
    // Remove leading 0 after 234 if present
    const formattedNumber = phoneNumber.startsWith("0")
      ? phoneNumber.slice(1)
      : phoneNumber;
    return `234${formattedNumber}`;
  }

  // For numbers starting with 234, handle the rest of the number
  const afterPrefix = digitsOnly.slice(3); // Get everything after 234
  // Remove leading 0 if present after 234
  const formattedAfterPrefix = afterPrefix.startsWith("0")
    ? afterPrefix.slice(1)
    : afterPrefix;

  // Limit to 13 digits (234 + 10 digit number)
  return `234${formattedAfterPrefix.slice(0, 10)}`;
}

/**
 * Validates a phone number
 * Requirements: Must start with 234, followed by 10 digits (no leading 0 after 234)
 */
export function validatePhoneNumber(phoneNumber: string): string | undefined {
  if (!phoneNumber) {
    return "Phone number is required";
  }

  // Format the phone number first
  const formatted = formatPhoneNumber(phoneNumber);

  // Validate format: 234 followed by exactly 10 digits
  if (!/^234[0-9]{10}$/.test(formatted)) {
    return "Phone number must start with 234 followed by 10 digits";
  }

  return undefined;
}

/**
 * Validates first name
 * Requirements: At least 2 characters, letters only (with spaces, hyphens, apostrophes)
 */
export function validateFirstName(firstName: string): string | undefined {
  if (!firstName.trim()) {
    return "First name is required";
  }
  if (firstName.length < 2) {
    return "First name must be at least 2 characters";
  }
  if (!/^[a-zA-Z\s-']+$/.test(firstName)) {
    return "First name can only contain letters, spaces, hyphens, and apostrophes";
  }
  return undefined;
}

/**
 * Validates last name
 * Requirements: At least 2 characters, letters only (with spaces, hyphens, apostrophes)
 */
export function validateLastName(lastName: string): string | undefined {
  if (!lastName.trim()) {
    return "Last name is required";
  }
  if (lastName.length < 2) {
    return "Last name must be at least 2 characters";
  }
  if (!/^[a-zA-Z\s-']+$/.test(lastName)) {
    return "Last name can only contain letters, spaces, hyphens, and apostrophes";
  }
  return undefined;
}
